import { Copy } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";

export const AspectRatioSidebar = ({
  aspectRatios,
  selectedAspectRatio,
  onSelect,
  graphicCounts,
  onCopyFrom,
  copyLoading,
}) => {
  const [copySource, setCopySource] = useState("");

  const AR_LABELS = {
    "1:1": "Square",
    "9:16": "Vertical / Stories",
    "4:5": "Instagram Feed",
    "16:9": "Landscape",
  };

  const otherRatios = aspectRatios.filter((ar) => ar !== selectedAspectRatio);

  return (
    <div className="lg:col-span-2 space-y-4" data-testid="aspect-ratio-sidebar">
      <h2 className="text-xs text-cyan-400/60 tracking-widest uppercase">
        Aspect Ratios
      </h2>
      <div className="space-y-1.5">
        {aspectRatios.map((ar) => (
          <button
            key={ar}
            onClick={() => onSelect(ar)}
            className={`w-full text-left p-3 rounded-sm border transition-all group ${
              selectedAspectRatio === ar
                ? "border-cyan-500/40 bg-cyan-500/[0.08] shadow-[0_0_15px_rgba(0,180,255,0.08)]"
                : "border-cyan-500/10 hover:border-cyan-500/20 hover:bg-cyan-500/[0.02]"
            }`}
            data-testid={`ar-tab-${ar}`}
          >
            <div className="flex items-center justify-between">
              <span
                className={`font-['Rajdhani'] text-base tracking-wide ${
                  selectedAspectRatio === ar ? "text-cyan-300" : "text-cyan-200/50"
                }`}
              >
                {ar}
              </span>
              {selectedAspectRatio === ar && graphicCounts[ar] !== undefined && (
                <span className="text-[10px] text-cyan-400/50 bg-cyan-500/10 px-1.5 py-0.5 rounded-sm">
                  {graphicCounts[ar]}
                </span>
              )}
            </div>
            <p className="text-[10px] text-cyan-200/25 mt-0.5">
              {AR_LABELS[ar] || "Custom"}
            </p>
          </button>
        ))}
      </div>

      {/* Copy Overlays */}
      {otherRatios.length > 0 && selectedAspectRatio && (
        <div className="border-t border-cyan-500/10 pt-4">
          <h3 className="text-[10px] text-cyan-400/40 mb-2 tracking-widest uppercase">
            Copy Overlays From
          </h3>
          <div className="flex gap-1.5">
            <Select value={copySource} onValueChange={setCopySource}>
              <SelectTrigger
                className="h-8 rounded-sm text-xs flex-1"
                data-testid="copy-source-select"
              >
                <SelectValue placeholder="Select ratio" />
              </SelectTrigger>
              <SelectContent className="bg-[#020a14] border-cyan-500/20 rounded-sm">
                {otherRatios.map((ar) => (
                  <SelectItem key={ar} value={ar}>
                    {ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => {
                if (copySource) {
                  onCopyFrom(copySource, selectedAspectRatio);
                  setCopySource("");
                }
              }}
              disabled={!copySource || copyLoading}
              className="h-8 px-3 rounded-sm border border-cyan-500/20 text-cyan-400/60 hover:text-cyan-300 hover:border-cyan-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs flex items-center gap-1"
              data-testid="copy-overlays-btn"
            >
              <Copy className="w-3 h-3" />
              {copyLoading ? "..." : "Copy"}
            </button>
          </div>
          <p className="text-[9px] text-cyan-200/15 mt-1.5">
            Duplicates all overlays from one ratio to the current
          </p>
        </div>
      )}

      <div className="border-t border-cyan-500/10 pt-4">
        <p className="text-[10px] text-cyan-200/20 leading-relaxed">
          Graphics added here are scoped to the selected aspect ratio. Switch
          ratios to add overlays independently per crop.
        </p>
      </div>
    </div>
  );
};
